import { useState } from 'react'
import type { ComponentType } from 'react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import {
  BanIcon,
  BellOffIcon,
  CheckSquareIcon,
  CopyIcon,
  EnvelopeIcon,
  ExternalLinkIcon,
  MoreHorizontalIcon,
  SearchIcon,
  StarOutlineIcon,
  TrashIcon,
} from '#/shared/ui/icons'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#/shared/ui/dropdown-menu'
import { toast } from '#/shared/ui/sonner'

import { usePhoneSelection } from '../model/selection'
import {
  ConversationActionType,
  useConversationActions,
} from '../model/use-conversation-actions'
import type { ActionTarget } from '../model/use-conversation-actions'
import { useConversationTarget } from '../model/use-conversation-target'
import type { RowActionProps } from './activity-row-actions'
import { DeleteConversationsDialog } from './delete-conversations-dialog'

// Match the design: 14px padded rows, gray glyphs on dark labels, soft popover.
const CONTENT_CLASS = cn(
  'w-53 rounded-xl bg-white/70 p-0 py-1.5 shadow-popover backdrop-blur-md',
  '[&_[data-slot=dropdown-menu-item]]:cursor-pointer [&_[data-slot=dropdown-menu-item]]:gap-2.5 [&_[data-slot=dropdown-menu-item]]:rounded-none [&_[data-slot=dropdown-menu-item]]:px-3.5 [&_[data-slot=dropdown-menu-item]]:py-2',
  '[&_[data-variant=default]_svg]:text-gray-500',
)

interface MenuRow {
  key: string
  icon: ComponentType<{ className?: string }>
  label: string
  onSelect?: () => void
  destructive?: boolean
}

type RunAction = (action: ConversationActionType) => () => void

/** Block + delete rows, split out to keep `useMenuRows` within its budget. */
function buildDangerRows(
  t: TFunction,
  run: RunAction,
  requestDelete: () => void,
  blocked?: boolean,
): MenuRow[] {
  return [
    {
      key: 'block',
      icon: BanIcon,
      label: t(blocked ? 'phonePanel.menu.unblock' : 'phonePanel.menu.block'),
      onSelect: run(
        blocked ? ConversationActionType.unblock : ConversationActionType.block,
      ),
    },
    {
      key: 'delete',
      icon: TrashIcon,
      label: t('phonePanel.menu.delete'),
      onSelect: requestDelete,
      destructive: true,
    },
  ]
}

/** Rows that act on the thread as a *view* rather than on its state — search,
 *  copy the number, open the order. Split out for the same reason as
 *  `buildDangerRows`: to keep `useMenuRows` within its line budget. */
function buildViewRows(
  t: TFunction,
  target: ActionTarget,
  onSearch?: () => void,
  onOpenOrder?: () => void,
): MenuRow[] {
  return [
    {
      key: 'search',
      icon: SearchIcon,
      label: t('phonePanel.search.inThread'),
      onSelect: onSearch,
    },
    {
      key: 'copy',
      icon: CopyIcon,
      label: t('phonePanel.menu.copy'),
      onSelect: () => copyNumber(t, target.phone),
    },
    {
      key: 'order',
      icon: ExternalLinkIcon,
      label: t('phonePanel.menu.openOrder'),
      onSelect: onOpenOrder,
    },
  ]
}

/**
 * Confirm-then-delete for a single thread. Deleting is irreversible, so the
 * menu row only opens the dialog; the mutation runs on confirm. When the
 * deleted thread is the one open in the panel (`chat`/`chatTo` in the URL),
 * the chat is closed back to the conversations list — otherwise the user is
 * left staring at a thread that no longer exists.
 */
function useDeleteConfirm(target: ActionTarget) {
  const [open, setOpen] = useState(false)
  const { apply, isPending } = useConversationActions()
  const { target: current, close } = useConversationTarget()

  const confirm = () => {
    void apply(ConversationActionType.delete, target).then((ok) => {
      if (!ok) return
      setOpen(false)
      const isCurrent =
        target.conversationId !== undefined
          ? target.conversationId === current?.conversationId
          : target.phone !== undefined && target.phone === current?.phone
      if (isCurrent) close()
    })
  }

  return { open, setOpen, confirm, pending: isPending }
}

function copyNumber(t: TFunction, phone: string | undefined): void {
  if (!phone) return
  void navigator.clipboard.writeText(phone)
  toast.success(t('phonePanel.menu.copied'))
}

/** Build the menu rows for a thread/number from its current flags. */
function useMenuRows(
  {
    itemId,
    target,
    starred,
    muted,
    blocked,
    unread,
    onOpenOrder,
    onSearch,
    hiddenActions,
  }: RowActionProps,
  requestDelete: () => void,
) {
  const { t } = useTranslation()
  const enterSelection = usePhoneSelection((state) => state.enter)
  const { apply } = useConversationActions()

  const run = (action: ConversationActionType) => () =>
    void apply(action, target)

  const rows: MenuRow[] = [
    ...buildViewRows(t, target, onSearch, onOpenOrder),
    {
      key: 'unread',
      icon: EnvelopeIcon,
      label: t(
        unread ? 'phonePanel.menu.markRead' : 'phonePanel.menu.markUnread',
      ),
      onSelect: run(
        unread ? ConversationActionType.read : ConversationActionType.unread,
      ),
    },
    {
      key: 'favorite',
      icon: StarOutlineIcon,
      label: t(
        starred ? 'phonePanel.menu.unfavorite' : 'phonePanel.menu.favorite',
      ),
      onSelect: run(
        starred
          ? ConversationActionType.unfavorite
          : ConversationActionType.favorite,
      ),
    },
    {
      key: 'mute',
      icon: BellOffIcon,
      label: t(muted ? 'phonePanel.menu.unmute' : 'phonePanel.menu.mute'),
      onSelect: run(
        muted ? ConversationActionType.unmute : ConversationActionType.mute,
      ),
    },
    {
      key: 'select',
      icon: CheckSquareIcon,
      label: t('phonePanel.menu.select'),
      onSelect: () => enterSelection(itemId),
    },
  ]
  const hidden = new Set(hiddenActions)
  // No order to open / nothing to search → drop the row rather than show a
  // dead item. Search is only ever wired from an open thread's header.
  if (!onOpenOrder) hidden.add('order')
  if (!onSearch) hidden.add('search')
  const keep = (row: MenuRow) => !hidden.has(row.key)
  return {
    rows: rows.filter(keep),
    dangerRows: buildDangerRows(t, run, requestDelete, blocked).filter(keep),
  }
}

function MenuRowItem({ row }: { row: MenuRow }) {
  const Icon = row.icon
  return (
    <DropdownMenuItem
      variant={row.destructive ? 'destructive' : 'default'}
      onSelect={row.onSelect}
    >
      <Icon />
      {row.label}
    </DropdownMenuItem>
  )
}

export function ActivityRowMenu({
  triggerClassName,
  triggerIcon: TriggerIcon = MoreHorizontalIcon,
  ...props
}: RowActionProps & {
  triggerClassName?: string
  /** Override the trigger glyph (e.g. a vertical kebab in the chat header). */
  triggerIcon?: ComponentType<{ className?: string }>
}) {
  const { t } = useTranslation()
  const deletion = useDeleteConfirm(props.target)
  const { rows, dangerRows } = useMenuRows(props, () => deletion.setOpen(true))

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t('phonePanel.menu.more')}
            className={cn(
              'flex shrink-0 cursor-pointer items-center justify-center rounded-full transition hover:brightness-95 focus-visible:outline-none',
              triggerClassName ?? 'size-7 bg-gray-200 text-gray-900',
            )}
          >
            <TriggerIcon className="size-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          data-phone-surface=""
          className={CONTENT_CLASS}
        >
          {rows.map((row) => (
            <MenuRowItem key={row.key} row={row} />
          ))}
          {dangerRows.length > 0 && (
            <DropdownMenuSeparator className="mx-0 bg-warm-100" />
          )}
          {dangerRows.map((row) => (
            <MenuRowItem key={row.key} row={row} />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <DeleteConversationsDialog
        open={deletion.open}
        count={1}
        pending={deletion.pending}
        onConfirm={deletion.confirm}
        onOpenChange={deletion.setOpen}
      />
    </>
  )
}
